import type {
  LeaveType,
  LeaveRule,
  LeaveApprovalFlow,
  ShortLeaveApprovalFlow,
  LeaveBalance,
  LeaveBalanceAdjustment,
  LeaveRequest,
  ShortLeaveRequest,
  LeaveCreditLedgerEntry,
  LeaveSettings,
  LeaveBlockedDate,
  PaginatedResponse,
} from '@pms/shared';
import type {
  CreateLeaveTypeInput,
  UpdateLeaveTypeInput,
  CreateLeaveRuleInput,
  UpdateLeaveRuleInput,
  SetApprovalFlowInput,
  SetShortLeaveFlowInput,
  CreateLeaveRequestInput,
  UpdateLeaveRequestInput,
  ReviewLeaveRequestInput,
  CreateShortLeaveInput,
  UpdateShortLeaveInput,
  ReviewShortLeaveInput,
  ListLeaveRequestsQuery,
  ListShortLeavesQuery,
  AdjustLeaveBalanceInput,
  CreateLeaveBlockedDatesInput,
  ListLeaveBlockedDatesQuery,
} from '@pms/shared';
import { api } from '@/lib/api';
import { downloadXlsx, localFileStamp } from '@/lib/download';

export interface TeamLeaveResponse extends PaginatedResponse<LeaveRequest> {
  awaiting_action: number;
}

export interface TeamShortLeaveResponse extends PaginatedResponse<ShortLeaveRequest> {
  awaiting_action: number;
}

export const leaveService = {
  // ── Leave Types ─────────────────────────────────────────────────────────────

  async listLeaveTypes(): Promise<LeaveType[]> {
    const res = await api.get<{ data: LeaveType[] }>('/leave/types');
    return res.data.data;
  },

  async createLeaveType(input: CreateLeaveTypeInput): Promise<LeaveType> {
    const res = await api.post<{ data: LeaveType }>('/leave/types', input);
    return res.data.data;
  },

  async updateLeaveType(id: string, input: UpdateLeaveTypeInput): Promise<LeaveType> {
    const res = await api.patch<{ data: LeaveType }>(`/leave/types/${id}`, input);
    return res.data.data;
  },

  async deleteLeaveType(id: string): Promise<void> {
    await api.delete(`/leave/types/${id}`);
  },

  // ── Leave Rules ─────────────────────────────────────────────────────────────

  async listLeaveRules(): Promise<LeaveRule[]> {
    const res = await api.get<{ data: LeaveRule[] }>('/leave/rules');
    return res.data.data;
  },

  async createLeaveRule(input: CreateLeaveRuleInput): Promise<LeaveRule> {
    const res = await api.post<{ data: LeaveRule }>('/leave/rules', input);
    return res.data.data;
  },

  async updateLeaveRule(id: string, input: UpdateLeaveRuleInput): Promise<LeaveRule> {
    const res = await api.patch<{ data: LeaveRule }>(`/leave/rules/${id}`, input);
    return res.data.data;
  },

  async deleteLeaveRule(id: string): Promise<void> {
    await api.delete(`/leave/rules/${id}`);
  },

  // ── Approval Flows ──────────────────────────────────────────────────────────

  async listApprovalFlows(): Promise<Record<string, LeaveApprovalFlow[]>> {
    const res = await api.get<{ data: Record<string, LeaveApprovalFlow[]> }>('/leave/flows');
    return res.data.data;
  },

  async setApprovalFlow(typeId: string, steps: SetApprovalFlowInput): Promise<LeaveApprovalFlow[]> {
    const res = await api.put<{ data: LeaveApprovalFlow[] }>(`/leave/flows/${typeId}`, steps);
    return res.data.data;
  },

  async listAnyOneApprovers(): Promise<Record<string, string[]>> {
    const res = await api.get<{ data: Record<string, string[]> }>('/leave/any-one-approvers');
    return res.data.data;
  },

  async setAnyOneApprovers(typeId: string, userIds: string[]): Promise<void> {
    await api.put(`/leave/any-one-approvers/${typeId}`, userIds);
  },

  async listShortLeaveFlows(): Promise<ShortLeaveApprovalFlow[]> {
    const res = await api.get<{ data: ShortLeaveApprovalFlow[] }>('/leave/short/flows');
    return res.data.data;
  },

  async setShortLeaveFlow(steps: SetShortLeaveFlowInput): Promise<ShortLeaveApprovalFlow[]> {
    const res = await api.put<{ data: ShortLeaveApprovalFlow[] }>('/leave/short/flows', steps);
    return res.data.data;
  },

  async listShortLeaveAnyOneApprovers(): Promise<string[]> {
    const res = await api.get<{ data: string[] }>('/leave/short/any-one-approvers');
    return res.data.data;
  },

  async setShortLeaveAnyOneApprovers(userIds: string[]): Promise<void> {
    await api.put('/leave/short/any-one-approvers', userIds);
  },

  // ── Leave Balances ──────────────────────────────────────────────────────────

  async getMyBalances(): Promise<LeaveBalance[]> {
    const res = await api.get<{ data: LeaveBalance[] }>('/leave/balances/me');
    return res.data.data;
  },

  async getUserBalances(userId: string): Promise<LeaveBalance[]> {
    const res = await api.get<{ data: LeaveBalance[] }>(`/leave/balances/user/${userId}`);
    return res.data.data;
  },

  async adjustBalance(
    userId: string,
    input: AdjustLeaveBalanceInput,
  ): Promise<{ accrued: number; available: number }> {
    const res = await api.post<{ data: { accrued: number; available: number } }>(
      `/leave/balances/user/${userId}/adjust`,
      input,
    );
    return res.data.data;
  },

  async getUserBalanceAdjustments(
    userId: string,
    page: number,
    limit: number,
  ): Promise<PaginatedResponse<LeaveBalanceAdjustment>> {
    const res = await api.get<PaginatedResponse<LeaveBalanceAdjustment>>(
      `/leave/balances/user/${userId}/adjustments`,
      { params: { page, limit } },
    );
    return res.data;
  },

  // ── Leave Requests ──────────────────────────────────────────────────────────

  async listMyRequests(query: Partial<ListLeaveRequestsQuery> = {}): Promise<PaginatedResponse<LeaveRequest>> {
    const res = await api.get<PaginatedResponse<LeaveRequest>>('/leave/requests/me', { params: query });
    return res.data;
  },

  async createLeaveRequest(input: CreateLeaveRequestInput): Promise<LeaveRequest> {
    const res = await api.post<{ data: LeaveRequest }>('/leave/requests', input);
    return res.data.data;
  },

  async uploadLeaveDocument(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post<{ data: { url: string } }>('/leave/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data.url;
  },

  async updateLeaveRequest(id: string, input: UpdateLeaveRequestInput): Promise<LeaveRequest> {
    const res = await api.patch<{ data: LeaveRequest }>(`/leave/requests/${id}`, input);
    return res.data.data;
  },

  async cancelLeaveRequest(id: string): Promise<void> {
    await api.patch(`/leave/requests/${id}/cancel`);
  },

  async adminDeleteLeaveRequest(id: string): Promise<void> {
    await api.delete(`/leave/requests/${id}`);
  },

  async listTeamRequests(query: Partial<ListLeaveRequestsQuery> = {}): Promise<TeamLeaveResponse> {
    const res = await api.get<TeamLeaveResponse>('/leave/requests/team', { params: query });
    return res.data;
  },

  async listAllRequests(query: Partial<ListLeaveRequestsQuery> = {}): Promise<PaginatedResponse<LeaveRequest>> {
    const res = await api.get<PaginatedResponse<LeaveRequest>>('/leave/requests', { params: query });
    return res.data;
  },

  async reviewLeaveRequest(id: string, input: ReviewLeaveRequestInput): Promise<void> {
    await api.patch(`/leave/requests/${id}/review`, input);
  },

  // ── Short Leave Requests ────────────────────────────────────────────────────

  async listMyShortLeaves(query: Partial<ListShortLeavesQuery> = {}): Promise<PaginatedResponse<ShortLeaveRequest>> {
    const res = await api.get<PaginatedResponse<ShortLeaveRequest>>('/leave/short/me', { params: query });
    return res.data;
  },

  async createShortLeave(input: CreateShortLeaveInput): Promise<ShortLeaveRequest> {
    const res = await api.post<{ data: ShortLeaveRequest }>('/leave/short', input);
    return res.data.data;
  },

  async updateShortLeave(id: string, input: UpdateShortLeaveInput): Promise<ShortLeaveRequest> {
    const res = await api.patch<{ data: ShortLeaveRequest }>(`/leave/short/${id}`, input);
    return res.data.data;
  },

  async cancelShortLeave(id: string): Promise<void> {
    await api.patch(`/leave/short/${id}/cancel`);
  },

  async adminDeleteShortLeave(id: string): Promise<void> {
    await api.delete(`/leave/short/${id}`);
  },

  async listTeamShortLeaves(query: Partial<ListShortLeavesQuery> = {}): Promise<TeamShortLeaveResponse> {
    const res = await api.get<TeamShortLeaveResponse>('/leave/short/team', { params: query });
    return res.data;
  },

  /**
   * Downloads the Leave Approvals export (.xlsx): Regular Leave, Short Leave and a
   * per-employee summary, scoped to what this reviewer sees on the page, every page.
   * `status: 'pending'` exports the pending tabs; omitting it exports history. The
   * server reads the data at request time, so every click is current.
   */
  async exportApprovalsXlsx(
    query: Partial<Pick<ListLeaveRequestsQuery, 'status' | 'search' | 'leave_type_id' | 'date_from' | 'date_to'>> = {},
  ): Promise<void> {
    await downloadXlsx(
      api.get('/leave/export/xlsx', { params: query, responseType: 'blob' }),
      `leave-approvals-${query.status ?? 'history'}-${localFileStamp()}.xlsx`,
    );
  },

  async listAllShortLeaves(query: Partial<ListShortLeavesQuery> = {}): Promise<PaginatedResponse<ShortLeaveRequest>> {
    const res = await api.get<PaginatedResponse<ShortLeaveRequest>>('/leave/short', { params: query });
    return res.data;
  },

  async reviewShortLeave(id: string, input: ReviewShortLeaveInput): Promise<void> {
    await api.patch(`/leave/short/${id}/review`, input);
  },

  // ── Credit Ledger ────────────────────────────────────────────────────────────

  async getMyLedger(leaveTypeId?: string, limit = 50): Promise<LeaveCreditLedgerEntry[]> {
    const res = await api.get<{ data: LeaveCreditLedgerEntry[] }>('/leave/balances/me/ledger', {
      params: { leave_type_id: leaveTypeId, limit },
    });
    return res.data.data;
  },

  // ── Admin: Credit Management ─────────────────────────────────────────────────

  async runAccrue(creditMonth?: string): Promise<{ inserted: number }> {
    const res = await api.post<{ data: { inserted: number } }>('/leave/admin/accrue', {
      credit_month: creditMonth,
    });
    return res.data.data;
  },

  async runYearlyReset(year?: number): Promise<{ reset: number }> {
    const res = await api.post<{ data: { reset: number } }>('/leave/admin/yearly-reset', { year });
    return res.data.data;
  },

  async getSettings(): Promise<LeaveSettings> {
    const res = await api.get<{ data: LeaveSettings }>('/leave/admin/settings');
    return res.data.data;
  },

  async updateSettings(input: LeaveSettings): Promise<LeaveSettings> {
    const res = await api.put<{ data: LeaveSettings }>('/leave/admin/settings', input);
    return res.data.data;
  },

  // ── Blocked Dates ───────────────────────────────────────────────────────────

  async listBlockedDates(query: ListLeaveBlockedDatesQuery = {}): Promise<LeaveBlockedDate[]> {
    const res = await api.get<{ data: LeaveBlockedDate[] }>('/leave/blocked-dates', { params: query });
    return res.data.data;
  },

  async addBlockedDates(input: CreateLeaveBlockedDatesInput): Promise<LeaveBlockedDate[]> {
    const res = await api.post<{ data: LeaveBlockedDate[] }>('/leave/blocked-dates', input);
    return res.data.data;
  },

  async deleteBlockedDate(id: string): Promise<void> {
    await api.delete(`/leave/blocked-dates/${id}`);
  },

  /** Years with leave data, newest first; `saved_at` is set when the year-end copy exists. */
  async listYearExports(): Promise<{ year: number; saved_at: string | null }[]> {
    const res = await api.get<{ data: { year: number; saved_at: string | null }[] }>(
      '/leave/admin/year-export',
    );
    return res.data.data;
  },

  /** One year's leave data (.xlsx): the copy saved at year end if there is one, otherwise live. */
  async downloadYearExportXlsx(year: number): Promise<void> {
    await downloadXlsx(
      api.get(`/leave/admin/year-export/${year}/xlsx`, { responseType: 'blob' }),
      `leave-data-${year}.xlsx`,
    );
  },
};
